import isEqual from 'lodash.isequal';
import { useLocation } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { useLocalStorage } from 'src/hooks/use-local-storage';

import { SettingsValueProps } from '../types';
import { SettingsContext } from './settings-context';

const STORAGE_KEY = 'settings';

type Props = {
  children: React.ReactNode;
  defaultSettings: SettingsValueProps;
};

export function SettingsProvider({ children, defaultSettings }: Readonly<Props>) {
  const [isInitialized, setIsInitialized] = useState(false);

  const { pathname } = useLocation();
  const { state, update, reset } = useLocalStorage(STORAGE_KEY, defaultSettings);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    setIsInitialized(true); // useState 기반 → 렌더 트리거 보장
  }, []);

  useEffect(() => {
    const DARK_MODE_PATH_PATTERNS = [/^\/learn\/[^/]+\/lesson\/[^/]+\/progression\/[^/]+$/];
    const isDarkThemePath = (path: string) =>
      DARK_MODE_PATH_PATTERNS.some((regex) => regex.test(path));
    const expectedMode = isDarkThemePath(pathname) ? 'dark' : 'light';

    update('themeMode', expectedMode);
  }, [pathname, state.themeMode, update]);

  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);
  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const canReset = !isEqual(state, defaultSettings);

  const memoizedValue = useMemo(
    () => ({
      ...state,
      onUpdate: update,
      canReset,
      onReset: reset,
      open: openDrawer,
      onToggle: onToggleDrawer,
      onClose: onCloseDrawer,
    }),
    [canReset, onCloseDrawer, onToggleDrawer, openDrawer, reset, state, update]
  );

  if (!isInitialized) return null; // 초기 렌더 차단

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
